/**
 * @file Stateful typed detail-route resolution.
 */

import { db } from "../firebase/firebase.js";
import { appData } from "../state/state.js";
import {
    parseDetailRouteCandidate,
} from "./detailRoutes.js";
import {
    getDetailRouteCollectionForItemType,
    getDetailRouteItemTypeForCollection,
} from "./detailRouteMetadata.js";
import {
    doc,
    getDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

export const DETAIL_ROUTE_RESOLUTION = Object.freeze({
    RESOLVED: "resolved",
    DENIED: "denied",
    MISSING: "missing",
    INVALID: "invalid",
    ERROR: "error",
});

function buildOutcome(status, values = {}) {
    return { status, ...values };
}

function normalizeTaskParentType(item) {
    const parentType = item?.parentType;
    if (parentType && getDetailRouteCollectionForItemType(parentType)) {
        return parentType;
    }

    return null;
}

function normalizeRouteTarget(routeTarget) {
    if (!routeTarget) {
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.INVALID);
    }

    if (typeof routeTarget === "string") {
        const routeCandidate = parseDetailRouteCandidate(routeTarget);
        if (!routeCandidate) {
            return buildOutcome(DETAIL_ROUTE_RESOLUTION.INVALID);
        }
        if (routeCandidate.status === "invalid") {
            return buildOutcome(DETAIL_ROUTE_RESOLUTION.INVALID);
        }
        return routeCandidate.target;
    }

    const collectionName = routeTarget.collectionName ||
        getDetailRouteCollectionForItemType(routeTarget.itemType);
    const itemType = routeTarget.itemType ||
        getDetailRouteItemTypeForCollection(collectionName);
    const itemId = routeTarget.itemId;

    if (!collectionName || !itemType || !itemId) {
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.INVALID);
    }

    if (getDetailRouteItemTypeForCollection(collectionName) !== itemType) {
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.INVALID);
    }

    return {
        attachmentId: routeTarget.attachmentId || null,
        collectionName,
        itemId,
        itemType,
        pathname: routeTarget.pathname ||
            `/${collectionName}/details/${itemId}`,
        versionId: routeTarget.versionId || null,
    };
}

function findCachedItem(collectionName, itemId, cache = appData) {
    const collectionItems = cache?.[collectionName];
    if (!Array.isArray(collectionItems)) {return null;}
    return collectionItems.find((item) => item?.id === itemId) || null;
}

function hydrateCache(collectionName, item, cache = appData) {
    if (!item?.id || !Array.isArray(cache?.[collectionName])) {return;}

    const existingIndex = cache[collectionName]
        .findIndex((entry) => entry?.id === item.id);
    if (existingIndex === -1) {
        cache[collectionName].push(item);
        return;
    }

    cache[collectionName][existingIndex] = item;
}

async function defaultReadDocument(collectionName, itemId) {
    const snapshot = await getDoc(doc(db, collectionName, itemId));
    if (!snapshot.exists()) {return null;}
    return { id: snapshot.id, ...snapshot.data() };
}

async function hydrateTaskParentContext(item, options = {}) {
    if (!item?.parentId) {
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.RESOLVED, {
            parentContext: { status: "none" },
        });
    }

    const cache = options.cache || appData;
    const parentType = normalizeTaskParentType(item);
    const collectionName = parentType ?
        getDetailRouteCollectionForItemType(parentType) :
        null;

    if (!parentType || !collectionName) {
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.DENIED, {
            parentContext: {
                status: "unavailable",
                parentId: item.parentId,
                parentType: parentType || null,
            },
        });
    }

    const cachedParent = findCachedItem(collectionName, item.parentId, cache);
    if (cachedParent) {
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.RESOLVED, {
            parentContext: {
                status: "resolved",
                item: cachedParent,
                parentId: item.parentId,
                parentType,
            },
        });
    }

    try {
        const readDocument = options.readDocument || defaultReadDocument;
        const parentItem = await readDocument(collectionName, item.parentId);
        if (!parentItem) {
            return buildOutcome(DETAIL_ROUTE_RESOLUTION.MISSING, {
                parentContext: {
                    status: "unavailable",
                    parentId: item.parentId,
                    parentType,
                },
            });
        }

        hydrateCache(collectionName, parentItem, cache);
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.RESOLVED, {
            parentContext: {
                status: "resolved",
                item: parentItem,
                parentId: item.parentId,
                parentType,
            },
        });
    } catch (error) {
        if (error?.code !== "permission-denied") {
            console.warn("Task parent context hydration failed:", error);
        } else {
            return buildOutcome(DETAIL_ROUTE_RESOLUTION.DENIED, {
                error,
                parentContext: {
                    status: "unavailable",
                    parentId: item.parentId,
                    parentType,
                },
            });
        }

        return buildOutcome(DETAIL_ROUTE_RESOLUTION.ERROR, {
            error,
            parentContext: {
                status: "unavailable",
                parentId: item.parentId,
                parentType,
            },
        });
    }
}

/**
 * Resolves a typed detail route against app state and Firestore.
 * @param {object|string} routeTarget Typed route object or pathname.
 * @param {object} [options] Resolution dependencies.
 * @param {Function} [options.readDocument] Optional document reader.
 * @param {object} [options.cache] Optional appData-compatible cache.
 * @returns {Promise<object>} Explicit resolver outcome.
 */
export async function resolveDetailRoute(routeTarget, options = {}) {
    const target = normalizeRouteTarget(routeTarget);
    if (target.status === DETAIL_ROUTE_RESOLUTION.INVALID) {
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.INVALID);
    }

    const cache = options.cache || appData;
    const cachedItem = findCachedItem(target.collectionName, target.itemId, cache);
    if (cachedItem) {
        const parentOutcome = target.itemType === "task" ?
            await hydrateTaskParentContext(cachedItem, options) :
            null;
        if (parentOutcome &&
            parentOutcome.status !== DETAIL_ROUTE_RESOLUTION.RESOLVED) {
            return buildOutcome(parentOutcome.status, {
                item: cachedItem,
                parentContext: parentOutcome.parentContext || null,
                routeTarget: target,
                error: parentOutcome.error,
            });
        }
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.RESOLVED, {
            item: cachedItem,
            parentContext: parentOutcome?.parentContext || null,
            routeTarget: target,
        });
    }

    try {
        const readDocument = options.readDocument || defaultReadDocument;
        const item = await readDocument(target.collectionName, target.itemId);
        if (!item) {
            return buildOutcome(DETAIL_ROUTE_RESOLUTION.MISSING, {
                routeTarget: target,
            });
        }

        hydrateCache(target.collectionName, item, cache);
        const parentOutcome = target.itemType === "task" ?
            await hydrateTaskParentContext(item, options) :
            null;
        if (parentOutcome &&
            parentOutcome.status !== DETAIL_ROUTE_RESOLUTION.RESOLVED) {
            return buildOutcome(parentOutcome.status, {
                item,
                parentContext: parentOutcome.parentContext || null,
                routeTarget: target,
                error: parentOutcome.error,
            });
        }
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.RESOLVED, {
            item,
            parentContext: parentOutcome?.parentContext || null,
            routeTarget: target,
        });
    } catch (error) {
        if (error?.code === "permission-denied") {
            return buildOutcome(DETAIL_ROUTE_RESOLUTION.DENIED, {
                error,
                routeTarget: target,
            });
        }

        console.error("Detail route resolution failed:", error);
        return buildOutcome(DETAIL_ROUTE_RESOLUTION.ERROR, {
            error,
            routeTarget: target,
        });
    }
}
